import { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Loader2, Plus, Search, Sparkles, Trash2 } from 'lucide-react'
import { useAdminCarFeatures, useCreateFeature, useDeleteFeature } from '../../../hooks/use-admin-cars'
import { DeleteConfirmDialog } from '../components/delete-confirm-dialog'

export function FeaturesPage() {
  const { data: features, isLoading } = useAdminCarFeatures()
  const createFeature = useCreateFeature()
  const deleteFeature = useDeleteFeature()

  const [search, setSearch] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL')
  const [open, setOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<any>(null)
  const [category, setCategory] = useState('Safety')
  const [name, setName] = useState('')
  const [code, setCode] = useState('')

  const filteredFeatures = (features || []).filter((f: any) => {
    if (search && !f.name.toLowerCase().includes(search.toLowerCase())) return false
    if (selectedCategory !== 'ALL' && f.category !== selectedCategory) return false
    return true
  })

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    const autoCode = code || name.toUpperCase().replace(/[^A-Z0-9]+/g, '_')
    await createFeature.mutateAsync({
      category,
      name,
      code: autoCode,
    })
    setName('')
    setCode('')
    setOpen(false)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Car Feature Catalog</h1>
          <p className="text-sm text-muted-foreground">Manage canonical feature taxonomy (Safety, Comfort, Tech, Entertainment).</p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger>
            <Button className="gap-2">
              <Plus className="size-4" /> Add New Feature
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Vehicle Feature</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 pt-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Category *</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium">
                  <option value="Safety">Safety</option>
                  <option value="Comfort">Comfort &amp; Convenience</option>
                  <option value="Technology">Technology &amp; Infotainment</option>
                  <option value="Performance">Performance &amp; Handling</option>
                  <option value="Exterior">Exterior Styling</option>
                  <option value="Interior">Interior Luxury</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Feature Name *</label>
                <Input placeholder="e.g. 360-Degree Camera / Panoramic Sunroof" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Feature Code (Optional)</label>
                <Input placeholder="AUTO_GENERATED" value={code} onChange={(e) => setCode(e.target.value)} />
              </div>

              <div className="pt-4 flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={createFeature.isPending}>
                  {createFeature.isPending ? <Loader2 className="size-4 animate-spin" /> : 'Save Feature'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filter & Search */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="relative flex-1 w-full max-w-md">
          <Search className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
          <Input placeholder="Search features..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        <div className="w-full sm:w-56">
          <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium">
            <option value="ALL">All Categories</option>
            <option value="Safety">Safety</option>
            <option value="Comfort">Comfort &amp; Convenience</option>
            <option value="Technology">Technology &amp; Infotainment</option>
            <option value="Performance">Performance</option>
            <option value="Exterior">Exterior</option>
            <option value="Interior">Interior</option>
          </select>
        </div>
      </div>

      {/* Features Table */}
      {isLoading ? (
        <div className="p-12 text-center flex flex-col items-center justify-center space-y-3">
          <Loader2 className="size-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading vehicle features catalog...</p>
        </div>
      ) : (
        <Card className="overflow-hidden border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-accent/40 text-muted-foreground border-b border-border uppercase text-[10px] tracking-wider">
              <tr>
                <th className="p-4">Feature Name</th>
                <th className="p-4">Category</th>
                <th className="p-4">Code</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredFeatures.map((f: any) => (
                <tr key={f.id} className="hover:bg-accent/20 transition-colors">
                  <td className="p-4 font-bold flex items-center gap-2">
                    <Sparkles className="size-4 text-amber-500" />
                    <span>{f.name}</span>
                  </td>
                  <td className="p-4">
                    <Badge variant="outline" className="text-xs font-semibold">{f.category}</Badge>
                  </td>
                  <td className="p-4 font-mono text-xs text-muted-foreground">{f.code}</td>
                  <td className="p-4">
                    <Badge className={f.isActive ? 'bg-emerald-500/20 text-emerald-500' : 'bg-muted text-muted-foreground'}>
                      {f.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="p-4 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setDeleteTarget(f)}
                      className="size-8 p-0 text-rose-500 hover:text-rose-600 hover:bg-rose-500/10"
                      title={`Delete feature "${f.name}"`}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {deleteTarget && (
        <DeleteConfirmDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={async () => {
            await deleteFeature.mutateAsync(deleteTarget.id)
          }}
          title={`Delete Feature: ${deleteTarget.name}`}
          entityName={deleteTarget.name}
          entityType="Feature"
          isIndependent={true}
          blockingReason={`Cannot delete feature "${deleteTarget.name}".`}
        />
      )}
    </div>
  )
}
